import React, { useEffect, useMemo } from 'react';
import type { CardPreviewPaneProps, GalleryAsset } from '../GenericPreviewPane';
import { AssetContainer, GenericPreviewPane } from '../GenericPreviewPane';
import { CardPreviewFavoriteButton } from '../CardCustomizationContent/CardPreviewFavoriteButton';
import { useCardPreviewPaneAssets } from '../useCardPreviewPaneAssets';
import { PremiumPDPReveal } from './PremiumPDPReveal';
import { InteractiveCardPreview } from '../CardCustomizationContent/InteractiveCardPreview';
import { Box, Flex } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { usePdpColorways } from '../../PdpColorwayContext';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { isBasicDigitalV2 } from '@apps/card/components/ProductUIConfig/isBasicDigitalV2';

export const CardPreviewPane = (props: CardPreviewPaneProps) => {
  const { tq } = useTranslation('stationery');
  const { cardJson, cardFront, thumbnailUrl, eventId, templateId, isFavorite, themeId, templateCategory } = props;
  const { cssVarStyle, preloadImages } = usePdpColorways();
  // Premium Digital and Basic Digital V2 share this pane; the active product picks the image set.
  const { activeProduct } = useStationeryProductPricingContext();
  const extraAssets = useCardPreviewPaneAssets('wedding', templateCategory, 'joy_digital', { productType: activeProduct.productType });
  // The envelope reveal is Premium Digital's preview; Basic Digital (V2) shows the card on its own.
  const showEnvelopeReveal = !isBasicDigitalV2(activeProduct);

  useEffect(() => {
    preloadImages?.();
  }, [preloadImages]);

  const previewContent = useMemo(() => {
    if (!showEnvelopeReveal) {
      return (
        <Box
          width="100%"
          height="100%"
          style={cssVarStyle}
        >
          <InteractiveCardPreview
            cardJson={cardJson}
            cardFront={cardFront}
          />
        </Box>
      );
    }
    return (
      <Flex
        flexDirection="column"
        marginY={7}
        marginX="auto"
        width="100%"
        alignItems="center"
        marginTop={-32}
      >
        <PremiumPDPReveal
          cardJSON={cardJson}
          cssVarStyle={cssVarStyle}
        />
      </Flex>
    );
  }, [cardJson, cardFront, cssVarStyle, showEnvelopeReveal]);

  const assets: Array<GalleryAsset> = useMemo(() => {
    return [
      {
        render: () => {
          return (
            <AssetContainer
              key="cardFront"
              label={tq`Front of the Card`}
              enforceAspectRatio={true}
              badge={
                <CardPreviewFavoriteButton
                  themeId={themeId}
                  templateCategory={templateCategory}
                  eventId={eventId || ''}
                  templateId={templateId}
                  isFavorite={isFavorite}
                />
              }
            >
              {previewContent}
            </AssetContainer>
          );
        },
        thumbnailUrl: thumbnailUrl,
      },
      ...extraAssets,
    ];
  }, [tq, thumbnailUrl, extraAssets, themeId, templateCategory, eventId, templateId, isFavorite, previewContent]);

  return (
    <GenericPreviewPane
      {...props}
      assets={assets}
    />
  );
};
